/* Paintly option auto-switch notice.
   Shared by the PBN storefront picker (assets/paintly-variant-picker.js) and the
   AI kit form (sections/paintly-ai-product-form-v2.liquid).

   Unavailable option combinations are no longer dead cards. Tapping one moves the
   OTHER options to the nearest combination that really exists, and this module
   shows the customer what moved, why, and what it costs, with an Undo button that
   puts the previous selection straight back.

   window.PaintlyOptSwitch.show({anchor, scope, title, reason, price, onUndo})
   window.PaintlyOptSwitch.clear(scope)

   The bar is inserted as a sibling right after `anchor` so it lands where the
   customer is already looking. Only one bar exists per scope at a time; the host
   clears it at the start of every subsequent tap, so Undo is only ever offered
   for the switch that just happened.

   The bar dismisses itself after DISMISS_MS and carries its own close button, so
   it never sits on the page as permanent furniture. The countdown pauses while a
   pointer is over it or while keyboard focus is inside it, which stops the bar
   disappearing out from under someone who is still reading it or reaching for
   Undo. Hover pause is only wired on devices that really hover, because a tap on
   a touchscreen fires mouseenter with no matching mouseleave and would otherwise
   freeze the bar on screen forever. */
(function () {
  if (window.PaintlyOptSwitch) return;

  var DISMISS_MS = 10000;
  var FADE_MS = 240;

  var CSS = [
    '.posw-bar{position:relative;display:flex;align-items:flex-start;gap:10px;margin:-10px 0 18px;padding:11px 44px 11px 12px;border-radius:13px;',
    'background:var(--lavsoft,#F3ECF7);border:1.5px solid var(--lavb,#CEA3D9);',
    "font-family:'Inter',system-ui,-apple-system,sans-serif;animation:posw-in .22s ease-out}",
    '@keyframes posw-in{from{opacity:0;transform:translateY(-5px)}to{opacity:1;transform:none}}',
    '@keyframes posw-out{from{opacity:1;transform:none}to{opacity:0;transform:translateY(-4px)}}',
    '.posw-bar.posw-out{animation:posw-out .24s ease-in forwards}',
    '.posw-ic{flex-shrink:0;width:21px;height:21px;margin-top:1px;border-radius:50%;background:var(--lavd,#9A6BB0);',
    'color:#fff;display:flex;align-items:center;justify-content:center}',
    '.posw-ic svg{width:12px;height:12px;display:block}',
    '.posw-txt{flex:1;min-width:0}',
    '.posw-title{display:block;font-size:13px;font-weight:800;line-height:1.32;color:var(--plum,#3B303E)}',
    '.posw-why{display:block;font-size:11.5px;font-weight:600;line-height:1.4;margin-top:3px;color:var(--grey,#6C6472)}',
    '.posw-price{display:block;font-size:11.5px;font-weight:700;line-height:1.4;margin-top:3px;color:var(--lavd,#9A6BB0)}',
    '.posw-undo{flex-shrink:0;align-self:center;min-height:40px;padding:10px 17px;border-radius:999px;cursor:pointer;',
    'border:1.5px solid var(--plum,#3B303E);background:#fff;color:var(--plum,#3B303E);',
    'font-family:inherit;font-size:12px;font-weight:800;letter-spacing:.3px;transition:.15s}',
    '.posw-undo:hover{background:var(--plum,#3B303E);color:#fff}',
    '.posw-undo:focus-visible{outline:2px solid var(--lavd,#9A6BB0);outline-offset:2px}',
    '.posw-x{position:absolute;top:5px;right:5px;width:34px;height:34px;padding:0;border:0;background:transparent;',
    'border-radius:50%;cursor:pointer;color:var(--grey,#6C6472);display:flex;align-items:center;justify-content:center;transition:.15s}',
    '.posw-x svg{width:13px;height:13px;display:block}',
    '.posw-x:hover{background:rgba(59,48,62,.10);color:var(--plum,#3B303E)}',
    '.posw-x:focus-visible{outline:2px solid var(--lavd,#9A6BB0);outline-offset:2px}',
    '@media (max-width:480px){.posw-bar{flex-wrap:wrap}.posw-undo{width:100%;margin-top:4px}}',
    '@media (prefers-reduced-motion:reduce){.posw-bar,.posw-bar.posw-out{animation:none}}',
    /* The -10px pull above exists to close the gap left by an option card's own
       bottom margin, so the bar reads as attached to the control it explains.
       When the thing directly above is a one-line caption instead of a card
       there is no gap to close, and the pull drags the bar up over the text.
       Seen on the AI kit page, where the shape note ("Showing portrait sizes,
       to match the shape of your design.") sits between the size dropdown and
       the bar and was getting its lower half clipped. */
    '.aip-sizenote + .posw-bar{margin-top:8px}'
  ].join('');

  var ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h16"/><path d="M14 6l6 6-6 6"/></svg>';
  var XICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round"><path d="M5 5l14 14"/><path d="M19 5L5 19"/></svg>';

  var canHover = !!(window.matchMedia && window.matchMedia('(hover:hover)').matches);

  var styled = false;
  function inject() {
    if (styled) return;
    styled = true;
    var s = document.createElement('style');
    s.setAttribute('data-posw', '1');
    s.textContent = CSS;
    (document.head || document.documentElement).appendChild(s);
  }

  function stop(bar) {
    if (bar && bar._poswT) { clearTimeout(bar._poswT); bar._poswT = null; }
  }

  function arm(bar) {
    stop(bar);
    if (!bar || !bar.parentNode) return;
    bar._poswT = setTimeout(function () { dismiss(bar); }, DISMISS_MS);
  }

  /* Fade then remove. Guarded so a double dismiss (X tapped just as the timer
     fires) cannot throw on an already-detached node. */
  function dismiss(bar) {
    if (!bar || bar._poswGone) return;
    bar._poswGone = true;
    stop(bar);
    if (!bar.parentNode) return;
    bar.classList.add('posw-out');
    setTimeout(function () {
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    }, FADE_MS);
  }

  function clear(scope) {
    var root = scope || document;
    var bars = root.querySelectorAll ? root.querySelectorAll('.posw-bar') : [];
    for (var i = 0; i < bars.length; i++) {
      stop(bars[i]);
      bars[i]._poswGone = true;
      bars[i].parentNode.removeChild(bars[i]);
    }
  }

  function show(o) {
    o = o || {};
    var anchor = o.anchor;
    if (!anchor || !anchor.parentNode) return null;
    inject();
    var scope = o.scope || anchor.parentNode;
    clear(scope);

    var bar = document.createElement('div');
    bar.className = 'posw-bar';
    bar.setAttribute('role', 'status');
    bar.setAttribute('aria-live', 'polite');

    var ic = document.createElement('span');
    ic.className = 'posw-ic';
    ic.setAttribute('aria-hidden', 'true');
    ic.innerHTML = ICON;
    bar.appendChild(ic);

    var txt = document.createElement('div');
    txt.className = 'posw-txt';
    var t = document.createElement('b');
    t.className = 'posw-title';
    t.textContent = o.title || '';
    txt.appendChild(t);
    if (o.reason) {
      var r = document.createElement('span');
      r.className = 'posw-why';
      r.textContent = o.reason;
      txt.appendChild(r);
    }
    if (o.price) {
      var p = document.createElement('span');
      p.className = 'posw-price';
      p.textContent = o.price;
      txt.appendChild(p);
    }
    bar.appendChild(txt);

    if (typeof o.onUndo === 'function') {
      var u = document.createElement('button');
      u.type = 'button';
      u.className = 'posw-undo';
      u.textContent = 'Undo';
      u.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        stop(bar);
        bar._poswGone = true;
        if (bar.parentNode) bar.parentNode.removeChild(bar);
        o.onUndo();
      });
      bar.appendChild(u);
    }

    var x = document.createElement('button');
    x.type = 'button';
    x.className = 'posw-x';
    x.setAttribute('aria-label', 'Close');
    x.innerHTML = XICON;
    x.addEventListener('click', function (e) {
      e.preventDefault();
      /* The host picker listens for clicks on the whole option UI and this bar
         lives inside that scope, so the close tap has to be kept to itself. */
      e.stopPropagation();
      dismiss(bar);
    });
    bar.appendChild(x);

    anchor.parentNode.insertBefore(bar, anchor.nextSibling);
    try { bar.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) { }

    if (canHover) {
      bar.addEventListener('mouseenter', function () { stop(bar); });
      bar.addEventListener('mouseleave', function () { arm(bar); });
    }
    bar.addEventListener('focusin', function () { stop(bar); });
    bar.addEventListener('focusout', function () { arm(bar); });
    arm(bar);

    return bar;
  }

  window.PaintlyOptSwitch = { show: show, clear: clear };
})();
